import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Cta from '../components/Cta';
import { CROPS, PACKING, MOLASSES } from '../data/agricultureContent';
import { waLink } from '../utils/whatsapp';

const STATS = [
  { n: 200, dur: 1600, suffix: { ar: 'مليون ج.م', en: 'Million EGP' }, label: { ar: 'رأس المال المدفوع', en: 'Paid-up capital' }, sub: { ar: 'بالكامل', en: 'Fully paid' } },
  { n: 500, dur: 1800, suffix: { ar: 'مليون ج.م', en: 'Million EGP' }, label: { ar: 'استثمارات مُدارة', en: 'Managed investments' }, sub: { ar: 'عبر القطاعات', en: 'Across sectors' } },
  { n: 1, dur: 1600, suffix: { ar: 'مليار ج.م', en: 'Billion EGP' }, label: { ar: 'إيرادات سنوية', en: 'Annual revenue' }, sub: { ar: 'إنتاج · تجارة · تصنيع', en: 'Production · Trade · Manufacturing' } },
  { n: 10, dur: 2000, suffix: { ar: 'آلاف+', en: 'Thousand+' }, label: { ar: 'فرصة عمل', en: 'Jobs' }, sub: { ar: 'مباشرة وغير مباشرة', en: 'Direct and indirect' } },
];

const SX_CARDS = [
  { to: '/sectors/agricultural-production', img: 'img_d29d5fb99275.webp', alt: { ar: 'حقول وإنتاج زراعي', en: 'Fields and agricultural production' }, idx: '٠١', title: { ar: 'قطاع الإنتاج الزراعي', en: 'Agricultural Production' }, desc: { ar: 'التنمية الزراعية واستصلاح الأراضي وإنتاج الحاصلات بأساليب علمية حديثة.', en: 'Agricultural development, land reclamation and crop production using modern scientific methods.' }, delay: '1', wide: false },
  { to: '/sectors/poultry-livestock', img: 'Screenshot_2026-06-28_at_12.28.14_PM.webp', alt: { ar: 'مزارع دواجن ومواشٍ', en: 'Poultry and livestock farms' }, idx: '٠٢', title: { ar: 'القطاع الداجني والحيواني', en: 'Poultry & Livestock' }, desc: { ar: 'محطات الدواجن، مزارع المواشي، وتطوير سلالات الماعز والأغنام.', en: 'Poultry stations, livestock farms, and the development of goat and sheep breeds.' }, delay: '2', wide: false },
  { to: '/sectors/manufacturing', img: 'image.webp', alt: { ar: 'العسل الأسود والمحوّل والمنتجات المصنّعة', en: 'Black honey, converted molasses and manufactured products' }, idx: '٠٣', title: { ar: 'قطاع التصنيع الزراعي', en: 'Agro-Manufacturing' }, desc: { ar: 'العسل الأسود المحوّل الصناعي، الأعلاف والزيوت، المبيدات والمخصبات الزراعية.', en: 'Black honey and converted molasses, animal feed and oils, agricultural pesticides and fertilizers.' }, delay: '3', wide: false },
  { to: '/sectors/logistics', img: 'truck-elhaware-3.webp', alt: { ar: 'أسطول النقل الثقيل والمواد السائلة', en: 'Heavy transport and liquid materials fleet' }, idx: '٠٤', title: { ar: 'قطاع الخدمات اللوجستية', en: 'Logistics Services' }, desc: { ar: 'النقل الثقيل والمواد السائلة بطاقة 1000 طن، وموازين الهواري المعتمدة عبر شبكة الصعيد.', en: 'Heavy transport and liquid materials with a 1,000-ton capacity, plus the certified Elhaware weighbridges across the Upper Egypt network.' }, delay: '2', wide: true },
  { to: '/sectors/commercial', img: 'img_641dd80b011d.webp', alt: { ar: 'التجارة والتصدير عبر الموانئ', en: 'Trade and export through ports' }, idx: '٠٥', title: { ar: 'القطاع التجاري والتصدير', en: 'Trade & Export' }, desc: { ar: 'التجارة المحلية، التصدير وتطوير الأعمال، والشراكات الاستثمارية في الخليج وأفريقيا.', en: 'Domestic trade, export and business development, and investment partnerships across the Gulf and Africa.' }, delay: '3', wide: true },
];

const CO_FILTERS = [
  { key: 'all', label: { ar: 'الكل', en: 'All' }, n: '11' },
  { key: 'production', label: { ar: 'الإنتاج الزراعي', en: 'Agricultural Production' } },
  { key: 'poultry', label: { ar: 'الداجني والحيواني', en: 'Poultry & Livestock' } },
  { key: 'manufacturing', label: { ar: 'التصنيع الزراعي', en: 'Agro-Manufacturing' } },
  { key: 'logistics', label: { ar: 'الخدمات اللوجستية', en: 'Logistics Services' } },
  { key: 'commercial', label: { ar: 'التجاري والتصدير', en: 'Trade & Export' } },
];

const COMPANIES = [
  { sector: 'manufacturing', img: 'img_d37078085212.webp', alt: { ar: 'شعار الهواري للعسل', en: 'Elhaware Honey logo' }, chip: { ar: 'التصنيع الزراعي', en: 'Agro-Manufacturing' }, name: { ar: 'الهواري للعسل البلدي والمحوّل', en: 'Elhaware for Black Honey & Converted Molasses' }, desc: { ar: 'الشركة الأم الحاضنة لكافة استثمارات المجموعة؛ إنتاج وتسويق العسل البلدي والمحوّل حتى الوصول بكل نشاطٍ إلى حجم تشغيلٍ مستقل.', en: 'The parent company that incubates all of the group\'s investments; producing and marketing black honey and converted molasses until each activity reaches independent operating scale.' } },
  { sector: 'manufacturing', img: 'img_64dad43cc6a2.webp', alt: { ar: 'شعار مصنع العصر للأعلاف', en: 'Al-Asr Feed Factory logo' }, chip: { ar: 'التصنيع الزراعي', en: 'Agro-Manufacturing' }, name: { ar: 'مصنع العصر للأعلاف', en: 'Al-Asr Feed Factory' }, desc: { ar: 'مصنع أعلاف بالمنطقة الصناعية في نجع حمادي بطاقة إنتاجية 5000 طن شهرياً من أعلاف الدواجن والمواشي.', en: 'A feed factory in the Nag Hammadi industrial zone with a production capacity of 5,000 tons per month of poultry and livestock feed.' } },
  { sector: 'manufacturing', img: 'img_3429590844e2.webp', alt: { ar: 'شعار جرين للمخصبات والمبيدات', en: 'Green for Fertilizers & Pesticides logo' }, chip: { ar: 'التصنيع الزراعي', en: 'Agro-Manufacturing' }, name: { ar: 'جرين للمخصّبات والمبيدات', en: 'Green for Fertilizers & Pesticides' }, desc: { ar: 'إنتاج وتسويق المغذّيات والمخصّبات الزراعية والمبيدات الحشرية والفطرية، بمصنعٍ في نجع حمادي ومكاتب توزيعٍ في الصعيد والقاهرة.', en: 'Producing and marketing agricultural nutrients and fertilizers as well as insecticides and fungicides, with a factory in Nag Hammadi and distribution offices in Upper Egypt and Cairo.' } },
  { sector: 'poultry', img: 'img_aaf3363a0456.webp', alt: { ar: 'شعار محطات الهواري للدواجن', en: 'Elhaware Poultry Stations logo' }, chip: { ar: 'الداجني والحيواني', en: 'Poultry & Livestock' }, name: { ar: 'محطات الهواري للدواجن', en: 'Elhaware Poultry Stations' }, desc: { ar: 'محطة تسمين بنظام البطاريات في محافظة قنا بطاقة 500 ألف طائر، باكورة محطات المجموعة نحو إنتاج مليون طائرٍ بالصعيد.', en: 'A battery-system fattening station in Qena governorate with a capacity of 500,000 birds — the first of the group\'s stations on the path to producing one million birds in Upper Egypt.' } },
  { sector: 'production', img: 'img_8fbabc2ce2e4.webp', alt: { ar: 'شعار الهواري للتنمية الزراعية', en: 'Elhaware Agricultural Development logo' }, chip: { ar: 'الإنتاج الزراعي', en: 'Agricultural Production' }, name: { ar: 'الهواري للتنمية الزراعية', en: 'Elhaware Agricultural Development' }, desc: { ar: 'التنمية الزراعية وإنتاج الحاصلات بأساليب علمية حديثة عبر أراضي ومزارع المجموعة.', en: 'Agricultural development and crop production using modern scientific methods across the group\'s lands and farms.' } },
  { sector: 'production', img: 'img_9365e97198a2.webp', alt: { ar: 'شعار كراست لاستصلاح الأراضي', en: 'Crust for Land Reclamation logo' }, chip: { ar: 'الإنتاج الزراعي', en: 'Agricultural Production' }, name: { ar: 'كراست لاستصلاح الأراضي', en: 'Crust for Land Reclamation' }, desc: { ar: 'استصلاح الأراضي وتجهيزها للإنتاج الزراعي وتوسيع الرقعة المزروعة ضمن خطط المجموعة.', en: 'Reclaiming and preparing land for agricultural production and expanding the cultivated area within the group\'s plans.' } },
  { sector: 'logistics', img: 'img_13410c036f05.webp', alt: { ar: 'شعار الهواري لنقل المواد البترولية', en: 'Elhaware Petroleum Transport logo' }, chip: { ar: 'الخدمات اللوجستية', en: 'Logistics Services' }, name: { ar: 'الهواري لنقل المواد البترولية والسائلة', en: 'Elhaware for Petroleum & Liquid Materials Transport' }, desc: { ar: 'نقل السوائل والمولاس والزيوت والمنتجات البترولية بطاقة تشغيلية 1000 طن، عبر وحدات نقلٍ ثقيل ونصف نقل.', en: 'Transporting liquids, molasses, oils and petroleum products with a 1,000-ton operating capacity, using heavy-transport and semi-trailer units.' } },
  { sector: 'logistics', img: 'img_a790bb04048e.webp', alt: { ar: 'شعار موازين الهواري', en: 'Elhaware Weighbridges logo' }, chip: { ar: 'الخدمات اللوجستية', en: 'Logistics Services' }, name: { ar: 'موازين الهواري', en: 'Elhaware Weighbridges' }, desc: { ar: 'وحدة موازين معتمدة ضمن منظومة الخدمات اللوجستية، تخدم عمليات النقل والتشغيل للمجموعة.', en: 'A certified weighbridge unit within the logistics services system, serving the group\'s transport and operating activities.' } },
  { sector: 'commercial', img: 'img_ebcfca53b93d.webp', alt: { ar: 'شعار الهواري للتجارة الداخلية', en: 'Elhaware Domestic Trade logo' }, chip: { ar: 'التجاري والتصدير', en: 'Trade & Export' }, name: { ar: 'الهواري للتجارة الداخلية', en: 'Elhaware for Domestic Trade' }, desc: { ar: 'التجارة المحلية وتوزيع منتجات المجموعة داخل السوق المصري عبر شبكةٍ من المنافذ والوكلاء.', en: 'Domestic trade and the distribution of the group\'s products within the Egyptian market through a network of outlets and agents.' } },
  { sector: 'commercial', img: 'elTasder.webp', alt: { ar: '', en: '' }, chip: { ar: 'التجاري والتصدير', en: 'Trade & Export' }, name: { ar: 'الهواري للتصدير وتطوير الأعمال', en: 'Elhaware for Export & Business Development' }, desc: { ar: 'التصدير وتطوير الأعمال وفتح أسواقٍ جديدة لمنتجات المجموعة عربياً وأفريقياً وأوروبياً.', en: 'Export and business development, opening new markets for the group\'s products across the Arab world, Africa and Europe.' } },
  { sector: 'commercial', img: 'img_3d6469d3b4da.webp', alt: { ar: 'شعار مبارك السعودي للاستثمار الزراعي', en: 'Mubarak Al-Saudi for Agricultural Investment logo' }, chip: { ar: 'التجاري والتصدير', en: 'Trade & Export' }, name: { ar: 'مبارك السعودي للاستثمار الزراعي', en: 'Mubarak Al-Saudi for Agricultural Investment' }, desc: { ar: 'تأسست بالمملكة العربية السعودية عام 2025 كشراكة استراتيجية لتوريد أجود المحاصيل والمنتجات المصرية للسوق السعودي بأعلى معايير الجودة العالمية.', en: 'Established in Saudi Arabia in 2025 as a strategic partnership to supply the finest Egyptian crops and products to the Saudi market at the highest international quality standards.' } },
];

const COUNTRIES = [
  [{ ar: 'مصر', en: 'Egypt' }, { ar: 'المقر الرئيسي · الصعيد والقاهرة', en: 'Headquarters · Upper Egypt & Cairo' }],
  [{ ar: 'السودان', en: 'Sudan' }, { ar: '650 فدان · الولايات الشمالية', en: '650 feddans · Northern States' }],
  [{ ar: 'السنغال', en: 'Senegal' }, { ar: 'مزارع دواجن · نصف مليون طائر', en: 'Poultry farms · half a million birds' }],
  [{ ar: 'السعودية', en: 'Saudi Arabia' }, { ar: 'استثمار زراعي · 2025', en: 'Agricultural investment · 2025' }],
  [{ ar: 'المغرب · ليبيا', en: 'Morocco · Libya' }, { ar: 'مزارع دواجن إنتاجية', en: 'Productive poultry farms' }],
  [{ ar: 'إنجلترا', en: 'England' }, { ar: 'توزيع المنتجات الصحية', en: 'Healthy products distribution' }],
];

const MEDIA_CARDS = [
  { url: 'https://www.alborsaanews.com/2023/12/25/1749850', img: 'news-alborsaa-2023.webp', kicker: { ar: 'جريدة البورصة · 25 ديسمبر 2023', en: 'Al-Borsa · 25 December 2023' }, title: { ar: 'تحالف مصرى يستثمر مليار جنيه لاستصلاح 20 ألف فدان بـ«الوادى الجديد»', en: 'Egyptian alliance invests EGP 1 billion to reclaim 20,000 feddans in the New Valley' }, delay: '1' },
  { url: 'https://www.youm7.com/story/2024/11/28/6793103', img: 'img_1588eb43db1e.webp', kicker: { ar: 'اليوم السابع · 28 نوفمبر 2024', en: 'Youm7 · 28 November 2024' }, title: { ar: 'استثمارات مصرية صينية في سوهاج في مجالات التصنيع الغذائي', en: 'Egyptian-Chinese investments in Sohag in the field of food manufacturing' }, delay: '1' },
  { url: 'https://www.albawabhnews.com/5108699', img: 'img_27413a7ffaf7.webp', kicker: { ar: 'البوابة نيوز · 28 نوفمبر 2024', en: 'Al-Bawaba News · 28 November 2024' }, title: { ar: 'هيثم الهواري: معرض «EGY AGRI» يحقق التكامل بين الإنتاج الزراعي والصناعي', en: 'Haitham Elhaware: The “EGY AGRI” exhibition achieves integration between agricultural and industrial production' }, delay: '2' },
  { url: 'https://regalalamal.com/329283/', img: 'img_a1a905cef779.webp', kicker: { ar: 'رجال الأعمال · 10 أكتوبر 2024', en: 'Rigal Al-Amal · 10 October 2024' }, title: { ar: 'هيثم الهواري: أشكر وزير المالية على إقرار التيسيرات ونطالب بخطوات جريئة لمساندة القطاع الخاص', en: 'Haitham Elhaware: I thank the Minister of Finance for approving the facilitations and call for bold steps to support the private sector' }, delay: '3' },
  { url: 'https://www.albawabhnews.com/5092097', img: 'img_9160552566a0.webp', kicker: { ar: 'البوابة نيوز · 28 نوفمبر 2024', en: 'Al-Bawaba News · 28 November 2024' }, title: { ar: 'مشاركة مصر في قمة بريكس خطوة إستراتيجية نحو تعزيز النمو الاقتصادي', en: 'Egypt\'s participation in the BRICS summit is a strategic step toward boosting economic growth' }, delay: '4' },
];

const FILM_EMBED = 'https://www.youtube.com/embed/Sv5Ah-FPndg?autoplay=1&rel=0&modestbranding=1&playsinline=1';
const FILM_POSTER = '/assets/img/img_e1644f345381.webp';
const filmEmbedSrc = () =>
  `${FILM_EMBED}&origin=${encodeURIComponent(window.location.origin)}`;

const HERO_VIDEO = '/assets/video/hero.mp4';
const HERO_VIDEO_POSTER = '/assets/video/hero-poster.jpg';

const CEO_PHOTOS = [
  { img: 'ceo-vineyard.webp', cap: { ar: 'متابعةٌ ميدانية لمزارع العنب', en: 'A field visit to the grape farms' } },
  { img: 'ceo-speech.webp', cap: { ar: 'كلمةٌ في احتفالية شباب سوهاج', en: 'A speech at the Sohag Youth celebration' } },
  { img: 'ceo-event.webp', cap: { ar: 'حضورٌ في الفعاليات الرسمية والمجتمعية', en: 'Attending official and community events' } },
  { img: 'ceo-honor.webp', cap: { ar: 'تكريمٌ وشراكات مع القيادات', en: 'Honors and partnerships with leaders' } },
  { img: 'ceo-factory.webp', cap: { ar: 'زيارةٌ لمنشآت التصنيع', en: 'A visit to the manufacturing facilities' } },
  { img: 'ceo-plant.webp', cap: { ar: 'جولةٌ في المصانع والمنشآت الصناعية', en: 'A tour of the factories and industrial facilities' } },
];

const CERTS = [
  { img: 'cert-1.jpg', name: { ar: 'رئيس مجلس الإدارة', en: 'Chairman of the Board' }, org: { ar: 'مجموعة الهواري', en: 'Elhaware Group' } },
  { img: 'cert-2.jpg', name: { ar: 'رئيس لجنة الزراعة', en: 'Head of the Agriculture Committee' }, org: { ar: 'الجمعية المصرية اللبنانية لرجال الأعمال', en: 'Egyptian-Lebanese Businessmen Association' } },
  { img: 'cert-3.jpg', name: { ar: 'عضو غرفة الصناعات الغذائية', en: 'Member — Chamber of Food Industries' }, org: { ar: 'اتحاد الصناعات المصرية', en: 'Federation of Egyptian Industries' } },
  { img: 'cert-4.jpg', name: { ar: 'عضو الجمعية المصرية الصينية لرجال الأعمال', en: 'Member — Egyptian-Chinese Businessmen Association' }, org: { ar: '', en: '' } },
  { img: 'cert-5.jpg', name: { ar: 'عضو الجمعية المصرية اللبنانية لرجال الأعمال', en: 'Member — Egyptian-Lebanese Businessmen Association' }, org: { ar: '', en: '' } },
];

function CertCard({ cert, isEN, t, onView }) {
  const [failed, setFailed] = useState(false);
  const src = `/assets/img/${cert.img}`;
  return (
    <button type="button" className="cert-card" onClick={() => { if (!failed) onView({ src, name: t(cert.name) }); }}>
      <span className="cert-card__frame">
        {failed ? (
          <span className="cert-card__ph">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8.5" r="6" /><path d="M8.5 13.5 7 22l5-2.6L17 22l-1.5-8.5" /></svg>
            <small>{isEN ? 'Certificate' : 'شهادة'}</small>
          </span>
        ) : (
          <>
            <img className="cert-card__img" loading="lazy" src={src} alt={t(cert.name)} onError={() => setFailed(true)} />
            <span className="cert-card__zoom" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3M11 8v6M8 11h6" /></svg>
            </span>
          </>
        )}
      </span>
      <span className="cert-card__name">{t(cert.name)}</span>
      {t(cert.org) ? <span className="cert-card__org">{t(cert.org)}</span> : null}
    </button>
  );
}

function CompanyLogo({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return <img className="co-card__logo" loading="lazy" src={`/assets/img/${src}`} alt={alt} onError={() => setFailed(true)} />;
}

export default function HomePage() {
  const { isEN, t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  const [playing, setPlaying] = useState(false);
  const [certView, setCertView] = useState(null);
  const heroVideoRef = useRef(null);
  const [heroMuted, setHeroMuted] = useState(true);
  const stopHeroAutoUnmuteRef = useRef(() => {});

  const toggleHeroSound = () => {
    const vid = heroVideoRef.current;
    if (!vid) return;
    stopHeroAutoUnmuteRef.current();
    vid.muted = !vid.muted;
    if (!vid.muted) {
      vid.volume = 0.3;
      vid.play().catch(() => {});
    }
  };

  useEffect(() => {
    const vid = heroVideoRef.current;
    if (!vid) return undefined;
    vid.volume = 0.3;

    const sync = () => setHeroMuted(vid.muted);
    vid.addEventListener('volumechange', sync);

    const events = ['pointerdown', 'touchstart', 'click', 'keydown'];
    const remove = () => events.forEach((e) => window.removeEventListener(e, unmute));
    stopHeroAutoUnmuteRef.current = remove;
    function unmute(e) {
      if (e && e.target && e.target.closest && e.target.closest('.hero-a__sound')) return;
      vid.muted = false;
      vid.volume = 0.3;
      const r = vid.play();
      if (r && typeof r.then === 'function') {
        r.then(remove).catch(() => {
          vid.muted = true;
          vid.play().catch(() => {});
        });
      } else {
        remove();
      }
    }

    vid.muted = false;
    const p = vid.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        vid.muted = true;
        vid.play().catch(() => {});
        events.forEach((e) => window.addEventListener(e, unmute, { passive: true }));
      });
    }

    return () => {
      vid.removeEventListener('volumechange', sync);
      remove();
    };
  }, []);

  useEffect(() => {
    if (!certView) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setCertView(null); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [certView]);

  useEffect(() => {
    if (!playing) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPlaying(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [playing]);

  useEffect(() => {
    document.title = isEN ? 'Elhaware Group — Home' : 'مجموعة الهواري — الصفحة الرئيسية';
  }, [isEN]);

  useEffect(() => {
    const shifts = Array.prototype.slice.call(document.querySelectorAll('.sx-card__shift'));
    if (!shifts.length) return undefined;
    let ticking = false;
    function update() {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      for (let i = 0; i < shifts.length; i++) {
        const fig = shifts[i].parentElement;
        const r = fig.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) continue;
        const center = r.top + r.height / 2;
        const prog = (center - vh / 2) / vh;
        let shift = -prog * r.height * 0.22;
        const max = r.height * 0.13;
        if (shift > max) shift = max;
        if (shift < -max) shift = -max;
        shifts[i].style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
      }
      ticking = false;
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <>
      <section className="hero-a">
        <div className="hero-a__bg">
          <div className="hero-a__slides" data-parallax="0.12">
            <img className="hero-a__img hero-a__slide is-active" src="/assets/img/img_bbb48514c78d.webp" alt={isEN ? 'Elhaware Group fields and farms' : 'حقول ومزارع مجموعة الهواري'} />
            <img className="hero-a__img hero-a__slide" src="/assets/img/img_81f9cdf6a82d.webp" alt="" />
          </div>
          <div className="hero-a__video-wrap" aria-hidden="true">
            <video ref={heroVideoRef} className="hero-a__video" src={HERO_VIDEO} poster={HERO_VIDEO_POSTER} autoPlay muted loop playsInline preload="auto" tabIndex="-1"></video>
          </div>
          <span className="hero-a__duo hero-a__duo--dark"></span>
          <span className="hero-a__duo hero-a__duo--light"></span>
          <span className="hero-a__glow"></span>
          <span className="hero-a__grain"></span>
          <span className="hero-a__legib"></span>
        </div>
        <div className="hero-a__inner wrap">
          <span className="eyebrow hero-a__eyebrow" data-reveal>
            {isEN ? 'A diversified agricultural investment group' : 'كيان اقتصادي متكامل الأنشطة'}
          </span>
          <div className="hero-a__cta" data-reveal data-delay="3">
            <Link className="btn btn-gold" to="/about"><span>{isEN ? 'Discover the group' : 'تعرّف على المجموعة'}</span> <span className="arr">←</span></Link>
            <Link className="btn btn-outline" to="/" state={{ scrollTo: 'sectors' }}>{isEN ? 'Our sectors' : 'قطاعاتنا'}</Link>
          </div>
        </div>
        <div className="hero-a__meta">
          <span className="scroll-cue">{isEN ? 'Scroll' : 'اكتشف المزيد'}</span>
          <span className="scroll-cue__line"></span>
        </div>
        <button
          type="button"
          className="hero-a__sound"
          onClick={toggleHeroSound}
          aria-pressed={!heroMuted}
          aria-label={
            heroMuted
              ? (isEN ? 'Unmute video' : 'تشغيل صوت الفيديو')
              : (isEN ? 'Mute video' : 'كتم صوت الفيديو')
          }
          title={
            heroMuted
              ? (isEN ? 'Unmute' : 'تشغيل الصوت')
              : (isEN ? 'Mute' : 'كتم الصوت')
          }
        >
          {heroMuted ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          )}
        </button>
      </section>

      <section className="stats-a section" style={{ paddingBlock: 0 }}>
        <div className="wrap">
          <div className="stats-a__grid">
            {STATS.map((s, i) => (
              <div className="stat-a" data-reveal data-delay={i ? String(i) : undefined} key={s.label.ar}>
                <div className="stat-a__num">
                  <span className="num-latin" data-count={s.n} data-dur={s.dur}>{s.n}</span>
                  <span className="stat-a__suffix">{t(s.suffix)}</span>
                </div>
                <div className="stat-a__label">{t(s.label)}</div>
                <div className="stat-a__sub">{t(s.sub)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-a section">
        <div className="wrap about-a__grid">
          <div className="about-a__media" data-reveal="right">
            <img className="about-a__img" src="/assets/img/img_5df9dd5088b5.webp" alt={isEN ? 'Dr. Haitham Ekry Elhaware — Chairman' : 'دكتورهيثم عكري الهواري — رئيس مجلس الإدارة'} />
            <div className="about-a__badge">
              <div className="num-latin">2015</div>
              <span>{isEN ? 'Year founded — and steady growth ever since' : 'عام التأسيس — ونموٌّ بخطواتٍ ثابتة منذ ذلك الحين'}</span>
            </div>
          </div>
          <div data-reveal="left">
            <span className="eyebrow">{isEN ? 'About' : 'من نحن'}</span>
            <h2>{isEN ? <><b>Elhaware</b> Group<br />an entity that bears trust</> : <>مجموعة <b>الهواري</b><br />كيانٌ يثمر ثقة</>}</h2>
            <p>{isEN
              ? 'Elhaware Group was founded in 2015 and has since grown into a diversified group of companies built on agricultural investment in all its branches — from crop production and livestock and poultry farming to agro-manufacturing and complementary activities — in harmony with our environmental, social and economic conditions.'
              : 'تأسست مجموعة الهواري عام 2015 ونمت منذ ذلك الحين لتصبح مجموعة شركات متنوعة المجالات، قائمة على الاستثمار الزراعي بفروعه — من إنتاج الحاصلات والإنتاج الحيواني والداجني إلى التصنيع الزراعي والأنشطة المكمّلة، بما يتناغم مع ظروفنا البيئية والاجتماعية والاقتصادية.'}</p>
            <div className="about-a__quote">
              <p>{isEN
                ? '“We strive to build a productive investment community that achieves real, sustainable development at the highest economic and operational efficiency.”'
                : '«نسعى إلى تكوين مجتمعٍ استثماري إنتاجي يحقّق تنميةً حقيقية مستدامة بأعلى فاعلية اقتصادية وتشغيلية.»'}</p>
            </div>
            <div className="signature">
              <span className="signature__mark">H. Elhaware</span>
              <span className="signature__who"><b>{isEN ? 'Dr. Haitham Ekry Elhaware' : 'دكتور / هيثم عكري الهواري'}</b><span>{isEN ? 'Chairman' : 'رئيس مجلس الإدارة'}</span></span>
            </div>
          </div>
        </div>
      </section>

      {false && (
      <section className="certs-a section" id="certifications">
        <div className="wrap">
          <div className="certs-a__head" data-reveal>
            <span className="eyebrow">{isEN ? 'Honors & Credentials' : 'شهاداتٌ وتكريمات'}</span>
            <h2>{isEN ? <>Dr. <b>Haitham Ekry Elhaware</b></> : <>دكتور<b>هيثم عكري الهواري</b></>}</h2>
            <p className="certs-a__intro">{isEN
              ? 'Positions, memberships and honors that reflect the Chairman\'s standing and experience across Egypt\'s agriculture and food-industry sectors.'
              : 'مناصبُ وعضوياتٌ وتكريماتٌ تشهد على مكانة رئيس مجلس الإدارة وخبرته في قطاعات الزراعة والصناعات الغذائية بمصر.'}</p>
          </div>
          <div className="certs-grid" data-reveal data-delay="1">
            {CERTS.map((c) => (
              <CertCard key={c.img} cert={c} isEN={isEN} t={t} onView={setCertView} />
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="sectors-a section" id="sectors">
        <div className="wrap">
          <div className="sectors-a__head" data-reveal>
            <div>
              <span className="eyebrow">{isEN ? 'Business Sectors' : 'قطاعات الأعمال'}</span>
              <h2>{isEN ? <>An <b>integrated</b> system from production to export</> : <>منظومة <b>متكاملة</b> من الإنتاج إلى التصدير</>}</h2>
            </div>
            <p style={{ color: 'rgba(231,236,223,.6)', maxWidth: '36ch' }}>{isEN ? 'Horizontal and vertical integration that reduces risk and ensures operational sustainability across the entire value chain.' : 'تكاملٌ أفقي ورأسي يخفّض المخاطر ويضمن استدامة التشغيل عبر سلسلة القيمة كاملة.'}</p>
          </div>

          <div className="sx-grid">
            {SX_CARDS.map((c) => (
              <article className={'sx-card' + (c.wide ? ' sx-card--wide' : '')} data-reveal data-delay={c.delay} key={c.to}>
                <Link className="sx-card__link" to={c.to} aria-label={t(c.title)} />
                <div className="sx-card__media"><div className="sx-card__shift"><img className="sx-card__img" loading="lazy" src={`/assets/img/${c.img}`} alt={t(c.alt)} /></div></div>
                <div className="sx-card__body">
                  <span className="sx-card__idx num-latin">{c.idx}</span>
                  <h3 className="sx-card__title">{t(c.title)}</h3>
                  <p className="sx-card__desc">{t(c.desc)}</p>
                  <div className="sx-card__actions">
                    <span className="sx-card__more">{isEN ? 'Sector details' : 'تفاصيل القطاع'} <span className="arr">←</span></span>
                    <a
                      className="sx-card__order"
                      href={waLink(isEN ? `Hello Elhaware Group, I'd like to order and inquire about the ${c.title.en}.` : `مرحباً مجموعة الهواري، أرغب في الطلب والاستفسار بخصوص ${c.title.ar}.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {isEN ? 'Contact us' : 'تواصل معنا'} <span className="arr">←</span>
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="crops-a section" id="crops">
        <div className="wrap">
          <div className="crops-a__head" data-reveal>
            <div>
              <span className="eyebrow">{isEN ? 'Agricultural Production' : 'الإنتاج الزراعي'}</span>
              <h2>{isEN ? <>Our crops from <b>the heart of the land</b></> : <>محاصيلنا من <b>قلب الأرض</b></>}</h2>
            </div>
            <p className="crops-a__intro">{isEN
              ? 'In the lands of Upper Egypt we grow a selection of high-quality fruit — from grapes, mango and pomegranate to citrus — and pack it fresh for the local market and export to the highest standards.'
              : 'نزرع في أراضينا  نخبةً من الفاكهة و الخضراوات عالية الجودة — من العنب والمانجو والرمان إلى الموالح و الخضراوات — ونعبّئها طازجةً للسوق المحلي والتصدير وفق أعلى المعايير.'}</p>
          </div>

          <div className="crops-grid">
            {CROPS.map((c, i) => (
              <article className="crop-card" style={{ '--crop-accent': c.accent }} data-reveal data-delay={String((i % 3) + 1)} key={c.key}>
                <div className="crop-card__media">
                  <img className="crop-card__img" loading="lazy" src={`/assets/img/${c.img}`} alt={isEN ? c.en : c.name} />
                  <span className="crop-card__tag">{isEN ? c.name : c.en}</span>
                </div>
                <div className="crop-card__body">
                  <h3 className="crop-card__name">{isEN ? c.en : c.name}</h3>
                  <p className="crop-card__desc">{t(c.desc)}</p>
                  <a
                    className="crop-card__order"
                    href={waLink(isEN ? `Hello Elhaware Group, I'd like to order ${c.en}.` : `مرحباً مجموعة الهواري، أودّ طلب ${c.name}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {isEN ? 'Contact us to order' : 'تواصل معنا للطلب'} <span className="arr">←</span>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="packing-a section" id="packing">
        <div className="wrap">
          <div className="crops-a__head" data-reveal>
            <div>
              <span className="eyebrow">{t(PACKING.eyebrow)}</span>
              <h2>{t(PACKING.title.before)}<b>{t(PACKING.title.bold)}</b></h2>
            </div>
            <p className="crops-a__intro">{t(PACKING.lead)}</p>
          </div>

          <div className="packing-a__gallery">
            {PACKING.photos.map((p) => (
              <figure className={'pack-shot ' + (p.wide ? 'pack-shot--wide' : 'pack-shot--third')} data-reveal key={p.img}>
                <img loading="lazy" src={`/assets/img/${p.img}`} alt={t(p.cap)} />
                <figcaption className="pack-shot__cap">{t(p.cap)}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="molasses-a section" id="molasses">
        <div className="wrap molasses-a__grid">
          <div data-reveal="right">
            <span className="eyebrow">{t(MOLASSES.eyebrow)}</span>
            <h2 className="molasses-a__title">{t(MOLASSES.title.before)}<b>{t(MOLASSES.title.bold)}</b></h2>
            <p className="molasses-a__lead">{t(MOLASSES.lead)}</p>
            <ul className="molasses-a__facts">
              {MOLASSES.facts.map((f) => <li key={f.ar || f}>{t(f)}</li>)}
            </ul>
          </div>
          <div className="molasses-a__media" data-reveal="left">
            <img className="molasses-a__img" loading="lazy" src={`/assets/img/${MOLASSES.img}`} alt={isEN ? 'Molasses — converted black honey' : 'المولاس — العسل الأسود المحوّل الصناعي'} />
            {MOLASSES.overlayImg && <img className="molasses-a__overlay" loading="lazy" src={`/assets/img/${MOLASSES.overlayImg}`} alt={isEN ? 'Sugarcane before the molasses process' : 'قصب السكر قبل عملية تحويل المولاس'} />}
            <div className="molasses-a__badge"><b className="num-latin">{MOLASSES.badge.big}</b><span>{t(MOLASSES.badge.text)}</span></div>
          </div>
        </div>
      </section>

      <section className="film-a section" id="film">
        <div className="wrap">
          <div className="film-a__head" data-reveal>
            <div>
              <span className="eyebrow">{isEN ? 'Corporate Film' : 'الفيلم التعريفي'}</span>
              <h2>{isEN ? <>A vision rooted in <b>the land</b><br />that bears <b>sustainable value</b></> : <>رؤيةٌ تتجذّر في <b>الأرض</b><br />وتثمر <b>قيمةً مستدامة</b></>}</h2>
            </div>
            <p className="film-a__intro">{isEN
              ? 'A brief journey inside Elhaware Group — from the land and farms to the factories and markets — telling the story of an integrated economic entity and its vision toward real, sustainable development.'
              : 'جولةٌ موجزة داخل مجموعة الهواري — من الأرض والمزارع إلى المصانع والأسواق — تروي قصة كيانٍ اقتصاديٍّ متكامل ورؤيته نحو تنميةٍ حقيقية مستدامة.'}</p>
          </div>

          <div className="film-a__frame" data-reveal>
            <button
              className="film-a__player"
              type="button"
              aria-label={isEN ? 'Play the Elhaware Group corporate film' : 'تشغيل الفيلم التعريفي لمجموعة الهواري'}
              onClick={() => setPlaying(true)}
            >
              <img className="film-a__poster" loading="lazy" src={FILM_POSTER} alt={isEN ? 'The Elhaware Group corporate film' : 'الفيلم التعريفي لمجموعة الهواري'} />
              <span className="film-a__scrim"></span>
              <span className="film-a__play"><span className="film-a__triangle"></span></span>
              <span className="film-a__caption">
                <span className="film-a__cap-text">{isEN ? 'Watch the corporate film' : 'شاهد الفيلم التعريفي للمجموعة'}</span>
              </span>
            </button>
          </div>
        </div>
      </section>

      <section className="ceo-a section" id="leadership">
        <div className="wrap">
          <div className="ceo-strip__head" data-reveal>
            <span className="eyebrow">{isEN ? 'Chairman' : 'رئيس مجلس الإدارة'}</span>
            <h3>{isEN ? <>Dr. Haitham Ekry Elhaware <b>at the heart of the action</b></> : <>دكتور/ هيثم عكري الهواري <b>في قلب الحدث</b></>}</h3>
            <p>{isEN ? 'Snapshots of his presence among the farms, factories, and official and community events.' : 'لقطاتٌ من حضوره بين المزارع والمصانع والفعاليات الرسمية والمجتمعية.'}</p>
          </div>
          <div className="ceo-grid">
            {CEO_PHOTOS.map((p) => (
              <figure className="ceo-shot" data-reveal key={p.img}>
                <img loading="lazy" src={`/assets/img/${p.img}`} alt={isEN ? `Dr. Haitham Ekry Elhaware — ${t(p.cap)}` : `دكتورهيثم عكري الهواري — ${t(p.cap)}`} />
                <figcaption className="ceo-shot__cap">{t(p.cap)}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="companies-a section" id="companies">
        <div className="wrap">
          <div className="companies-a__head" data-reveal>
            <div>
              <span className="eyebrow">{isEN ? 'Group Companies' : 'شركات المجموعة'}</span>
              <h2>{isEN ? <>Specialized entities<br />under <b>one umbrella</b></> : <>كياناتٌ متخصّصة<br />تحت <b>مظلّةٍ واحدة</b></>}</h2>
            </div>
            <p className="companies-a__intro">{isEN
              ? 'A system of integrated companies covering the entire value chain — from land and production to manufacturing, transport, trade and export, inside Egypt and across the African and Gulf depths.'
              : 'منظومةٌ من الشركات المتكاملة تغطّي سلسلة القيمة كاملةً — من الأرض والإنتاج إلى التصنيع والنقل والتجارة والتصدير، داخل مصر وفي العمق الأفريقي والخليجي.'}</p>
          </div>

          <div className="co-filter" data-reveal data-delay="1" role="tablist" aria-label={isEN ? 'Filter by sector' : 'تصفية حسب القطاع'}>
            {CO_FILTERS.map((f) => (
              <button
                className={'co-chip' + (activeFilter === f.key ? ' is-active' : '')}
                type="button"
                onClick={() => setActiveFilter(f.key)}
                key={f.key}
              >
                {t(f.label)}{f.n ? <span className="co-chip__n"> {f.n}</span> : null}
              </button>
            ))}
          </div>

          <div className="co-grid" data-reveal data-delay="2">
            {COMPANIES.map((c) => {
              const hidden = activeFilter !== 'all' && c.sector !== activeFilter;
              return (
                <article className={'co-card' + (hidden ? ' is-hidden' : '')} data-sector={c.sector} key={c.name.ar}>
                  <div className="co-card__plate">
                    <CompanyLogo src={c.img} alt={t(c.alt)} />
                    <span className="co-card__mono">ه</span>
                  </div>
                  <span className="co-card__chip">{t(c.chip)}</span>
                  <h3 className="co-card__name">{t(c.name)}</h3>
                  <p className="co-card__desc">{t(c.desc)}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="global-a section" id="global">
        <div className="wrap global-a__grid">
          <div data-reveal="right">
            <span className="eyebrow">{isEN ? 'International Presence' : 'الفروع الخارجية'}</span>
            <h2>{isEN ? <>A presence spanning <b>six countries</b></> : <>حضورٌ يمتد عبر <b>ست دول</b></>}</h2>
            <p>{isEN
              ? 'The group is pursuing international expansion primarily through strategic companies in the African depths, working to strengthen production and maximize value-added resources for the host country — leveraging our distinguished accumulated expertise across the group\'s productive agricultural sectors, in exchange for fair, balanced trade. This is embodied in Sudan with 650 feddans in the Northern States, and poultry farms in Senegal with a targeted operating capacity of half a million eggs per month and half a million birds per fattening cycle, alongside targeting the key consumer markets with the highest purchasing power, whether in the Gulf region (Saudi Arabia) or Europe (Britain).'
              : 'تستهدف المجموعة التوسّع الخارجي عبر شركاتٍ استراتيجية في العمق الأفريقي بشكلٍ أساسي، تعمل على تدعيم الإنتاج وتعظيم موارد القيمة المضافة للبلد المضيف — استفادةً من خبراتنا المتراكمة المتميّزة في قطاعات المجموعة الإنتاجية بمجالات الزراعة، مقابل تبادلٍ تجاريٍّ متكافئ. ويتجسّد ذلك في السودان بـ650 فداناً في الولايات الشمالية، ومزارع دواجن في السنغال بطاقةٍ تشغيلية مستهدفة نصف مليون بيضة شهرياً ونصف مليون طائرٍ في دورة التسمين، إلى جانب استهداف الأسواق الاستهلاكية الأساسية والأعلى في القوة الشرائية، سواء في منطقة الخليج (السعودية) أو أوروبا (بريطانيا).'}</p>
            <div className="country-list">
              {COUNTRIES.map(([name, detail]) => (
                <div className="country" key={name.ar}>
                  <span className="country__dot"></span>
                  <div><div className="country__name">{t(name)}</div><div className="country__detail">{t(detail)}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div className="map-a" data-reveal="left">
            <div className="ph shot" role="img" aria-label={isEN ? 'International presence map' : 'خريطة الحضور الدولي'} style={{ backgroundImage: "url('/assets/img/img_3ea7ee40b961.webp')" }}></div>
            <span className="map-node" style={{ top: '46%', insetInlineStart: '52%' }}></span>
            <span className="map-node" style={{ top: '54%', insetInlineStart: '48%' }}></span>
            <span className="map-node" style={{ top: '60%', insetInlineStart: '38%' }}></span>
            <span className="map-node" style={{ top: '40%', insetInlineStart: '58%' }}></span>
            <span className="map-node" style={{ top: '24%', insetInlineStart: '46%' }}></span>
          </div>
        </div>
      </section>

      <section className="media-a section" id="media">
        <div className="wrap">
          <div style={{ textAlign: 'center', maxWidth: '60ch', marginInline: 'auto' }} data-reveal>
            <span className="eyebrow" style={{ justifyContent: 'center' }}>{isEN ? 'Media Center' : 'المركز الإعلامي'}</span>
            <h2 style={{ fontSize: 'clamp(30px,4vw,50px)', fontWeight: 300, margin: '18px 0 0', color: 'var(--forest-900)' }}>{isEN ? <>The latest the <b style={{ fontWeight: 800 }}>press</b> has said about the group</> : <>أحدث ما ذكرته <b style={{ fontWeight: 800 }}>الصحافة</b> عن المجموعة</>}</h2>
          </div>
          <div className="media-a__grid">
            {MEDIA_CARDS.map((m) => (
              <article
                className="media-card"
                data-reveal
                data-delay={m.delay}
                onClick={() => window.open(m.url, '_blank')}
                style={{ cursor: 'pointer' }}
                key={m.url}
              >
                <div className="ph ph-light shot" role="img" aria-label={isEN ? 'News' : 'خبر'} style={{ backgroundImage: `url('/assets/img/${m.img}')` }}></div>
                <div className="media-card__body">
                  <span className="media-card__kicker">{t(m.kicker)}</span>
                  <h3 className="media-card__title">{t(m.title)}</h3>
                  <a className="media-card__link" href={m.url} target="_blank" rel="noopener noreferrer">{isEN ? 'Read more' : 'اقرأ الخبر'} <span className="arr">←</span></a>
                </div>
              </article>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }} data-reveal>
            <Link className="btn btn-gold" to="/media"><span>{isEN ? 'Browse the Media Center' : 'تصفّح المركز الإعلامي'}</span> <span className="arr">←</span></Link>
          </div>
        </div>
      </section>

      <Cta secondaryTo="/about" secondaryLabel={isEN ? 'Our vision & strategy' : 'رؤيتنا واستراتيجيتنا'} />

      <div
        className={'cert-lightbox' + (certView ? ' is-open' : '')}
        aria-hidden={certView ? 'false' : 'true'}
        onClick={(e) => { if (e.target === e.currentTarget) setCertView(null); }}
      >
        <button className="cert-lightbox__close" type="button" aria-label={isEN ? 'Close' : 'إغلاق'} onClick={() => setCertView(null)}>✕</button>
        {certView && (
          <figure className="cert-lightbox__fig">
            <img src={certView.src} alt={certView.name} />
            <figcaption>{certView.name}</figcaption>
          </figure>
        )}
      </div>

      <div
        className={'mc-lightbox' + (playing ? ' is-open' : '')}
        aria-hidden={playing ? 'false' : 'true'}
        onClick={(e) => { if (e.target === e.currentTarget) setPlaying(false); }}
      >
        <button className="mc-lightbox__close" type="button" aria-label={isEN ? 'Close' : 'إغلاق'} onClick={() => setPlaying(false)}>✕</button>
        <div className="mc-lightbox__frame">
          <div className="mc-lightbox__stage">
            {playing && (
              <div className="mc-lightbox__video">
                <iframe src={filmEmbedSrc()} title={isEN ? 'The Elhaware Group corporate film' : 'الفيلم التعريفي لمجموعة الهواري'} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen></iframe>
              </div>
            )}
          </div>
          <p className="mc-lightbox__cap">{isEN ? 'Elhaware Group — Corporate Film' : 'مجموعة الهواري — الفيلم التعريفي'}</p>
        </div>
      </div>
    </>
  );
}
